import { C } from '../tokens';
import type { SyncStatus } from '../../domains/sync/types';

interface Props {
  sync: SyncStatus;
  /**
   * Прямое действие на баннере (исторически — flushOutbox). С 1.21.0
   * остаётся для обратной совместимости с inline-вызовами; в глобальном
   * SyncBar в AppShell это поле не используется — вся карточка ведёт в
   * SyncQueue через `onOpenQueue`.
   */
  onSync?: () => void;
  pendingCount?: number;
  /**
   * 1.18.0 — dead-letter count. Если > 0, баннер красный «N не отправлено,
   * нужно вмешательство» вместо обычного «Ошибка синхронизации». CTA
   * открывает экран dead-letter (придёт в 1.18.x — пока onSync делает
   * runSyncRecoveryPass).
   */
  deadLetterCount?: number;
  onOpenDeadLetter?: () => void;
  /**
   * 1.21.0 — переход на экран очереди синхронизации. Когда задан,
   * **вся карточка** ведёт в очередь (а не запускает flushOutbox), и
   * текстовый CTA меняется на «Подробнее →». Используется в глобальном
   * SyncBar в AppShell, чтобы пользователь мог посмотреть детали и
   * выполнить ручные действия (повторить, удалить) вместо угадывания
   * «что там в очереди» по одному статусу.
   */
  onOpenQueue?: () => void;
}

export function SyncBar({ sync, onSync, pendingCount = 0, deadLetterCount = 0, onOpenDeadLetter, onOpenQueue }: Props) {
  if (sync === 'synced') return null;

  // 1.18.0 — Если есть dead-letter, у него приоритет над обычным error.
  // Это самый важный сигнал — данные не уйдут сами, нужно действие.
  if (deadLetterCount > 0) {
    const handler = onOpenDeadLetter ?? onOpenQueue ?? onSync;
    return (
      <div
        onClick={handler}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '8px 16px',
          background: C.reS,
          borderBottom: `1px solid ${C.re}`,
          cursor: handler ? 'pointer' : 'default',
          flexShrink: 0,
        }}
      >
        <span style={{ color: C.re, fontSize: 12, fontWeight: 700, display: 'inline-block' }}>⚠</span>
        <span style={{ fontSize: 11, color: C.re, fontWeight: 500, flex: 1 }}>
          {deadLetterCount === 1
            ? '1 запись не отправлена — нужно действие'
            : `${deadLetterCount} записей не отправлены — нужно действие`}
        </span>
        {handler ? <span style={{ fontSize: 11, color: C.re, fontWeight: 700 }}>Открыть →</span> : null}
      </div>
    );
  }

  // 1.21.0 — если задан onOpenQueue, вся карточка ведёт в очередь; CTA
  // меняется на «Подробнее →». Это поведение по умолчанию в глобальном
  // SyncBar в AppShell.
  const openQueueMode = !!onOpenQueue;

  const map = {
    pending: { bg: C.amS, bd: C.am, c: C.am, ic: '⚡', tx: `${pendingCount} записей ждут отправки`, cta: openQueueMode ? 'Подробнее' : 'Отправить' },
    syncing: { bg: C.blS, bd: C.bl, c: C.bl, ic: '↻', tx: 'Синхронизация...', cta: openQueueMode ? 'Подробнее' : null },
    offline: { bg: C.amS, bd: C.am, c: C.am, ic: '○', tx: 'Офлайн — данные сохраняются локально', cta: openQueueMode ? 'Подробнее' : null },
    // 1.18.0 — здесь только retryable failed (dead_letter уже обработан выше).
    // Текст «повторяем автоматически» — потому что они реально будут
    // повторены по next_retry_at.
    error: { bg: C.amS, bd: C.am, c: C.am, ic: '↻', tx: 'Синхронизация задерживается, повторяем автоматически', cta: openQueueMode ? 'Подробнее' : 'Повторить' },
    synced: { bg: C.grS, bd: C.gr, c: C.gr, ic: '✓', tx: 'Синхронизировано', cta: null },
  } as const;

  const m = map[sync] ?? map.pending;
  const handler = openQueueMode ? onOpenQueue : (m.cta ? onSync : undefined);
  return (
    <div
      onClick={handler}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '8px 16px',
        background: m.bg,
        borderBottom: `1px solid ${m.bd}`,
        cursor: handler ? 'pointer' : 'default',
        flexShrink: 0,
      }}
    >
      <span style={{ color: m.c, fontSize: 12, fontWeight: 700, animation: sync === 'syncing' ? 'sp 1s linear infinite' : 'none', display: 'inline-block' }}>{m.ic}</span>
      <span style={{ fontSize: 11, color: m.c, fontWeight: 500, flex: 1 }}>{m.tx}</span>
      {m.cta ? <span style={{ fontSize: 11, color: m.c, fontWeight: 700 }}>{m.cta} →</span> : null}
    </div>
  );
}
